/**
 * glb-internals — the plumbing BOTH exporters share, defined once (ADR authoring
 * policy §4: one canonical definition): the fail-loud error shape, the Node
 * FileReader shim GLTFExporter's binary path needs in CI, and the JSON-chunk
 * reader the post-write structural asserts parse. Not exported from the package
 * index — `export-glb` re-exports the error so the public surface is unchanged.
 * Depends on: nothing. Depended on by: export-glb, export-animation-only.
 */

/** A cannot-export condition — refused whole, every issue named (ADR-010 §7). */
export class GlbExportError extends Error {
  constructor(readonly issues: readonly string[]) {
    super(`Cannot export GLB (${issues.length} issue(s)):\n` + issues.map((i) => `  - ${i}`).join('\n'));
    this.name = 'GlbExportError';
  }
}

const GLB_MAGIC = 0x46546c67; // 'glTF'
const CHUNK_JSON = 0x4e4f534a; // 'JSON'

/**
 * GLTFExporter's binary path reads its output Blob through FileReader, which
 * Node lacks; a minimal shim is installed for the duration of the write when
 * (and only when) the global is absent, so headless proofs can run in CI.
 */
export function installFileReaderShim(): (() => void) | null {
  const g = globalThis as { FileReader?: unknown };
  if (typeof g.FileReader !== 'undefined') return null;
  class NodeFileReader {
    result: ArrayBuffer | string | null = null;
    onloadend: (() => void) | null = null;
    readAsArrayBuffer(blob: Blob): void {
      void blob.arrayBuffer().then((ab) => {
        this.result = ab;
        this.onloadend?.();
      });
    }
    readAsDataURL(blob: Blob): void {
      void blob.arrayBuffer().then((ab) => {
        const base64 = Buffer.from(ab).toString('base64');
        this.result = `data:${blob.type || 'application/octet-stream'};base64,${base64}`;
        this.onloadend?.();
      });
    }
  }
  g.FileReader = NodeFileReader;
  return () => {
    delete g.FileReader;
  };
}

/** The glTF top-level sections the structural asserts inspect. */
export interface GlbJsonChunk {
  scenes?: unknown[];
  animations?: readonly unknown[];
  meshes?: readonly unknown[];
  skins?: readonly unknown[];
  materials?: readonly unknown[];
  textures?: readonly unknown[];
  images?: readonly unknown[];
}

/** Decode the JSON chunk of a GLB — the post-write structural asserts read it. */
export function glbJson(glb: ArrayBuffer): GlbJsonChunk {
  const dv = new DataView(glb);
  if (glb.byteLength < 20 || dv.getUint32(0, true) !== GLB_MAGIC) {
    throw new GlbExportError(['exporter produced bytes without the glTF magic — not a GLB']);
  }
  const jsonLength = dv.getUint32(12, true);
  if (dv.getUint32(16, true) !== CHUNK_JSON) {
    throw new GlbExportError(['first GLB chunk is not JSON — malformed container']);
  }
  return JSON.parse(new TextDecoder().decode(new Uint8Array(glb, 20, jsonLength))) as GlbJsonChunk;
}
